import React from "react";
import { Order } from "../interfaces/Order";
import { groupProducts, getNormalizedProducts } from "../global/utils";

interface ComandaReceiptProps {
  order: Order;
}

export const ComandaReceipt = React.forwardRef<HTMLDivElement, ComandaReceiptProps>(({ order }, ref) => {
  const formatDate = (dateValue: any) => {
    let date = new Date();
    if (dateValue) {
      if (typeof dateValue.toDate === "function") date = dateValue.toDate();
      else if (dateValue instanceof Date) date = dateValue;
      else date = new Date(dateValue);
    }

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    const strTime = String(hours).padStart(2, "0") + ":" + minutes + " " + ampm;

    return `${day}/${month}/${year} ${strTime}`;
  };

  const cleanName = (str?: string) => {
    if (!str) return "";
    const cleaned = str.includes("@") ? str.split("@")[0] : str;
    return cleaned.trim().toUpperCase();
  };

  const getOrderTypeLabel = (type?: string) => {
    switch (type) {
      case "domicilio":
        return "DOMICILIO";
      case "mesa":
        return `MESA ${order.tableNumber || ""}`;
      case "llevar":
        return "PARA LLEVAR";
      case "comerAca":
        return "COMER AQUI";
      default:
        return (type || "PEDIDO").toUpperCase();
    }
  };

  const formatVariant = (variantLabel?: string) => {
    if (!variantLabel) return null;

    // Si contiene indicación de mitades
    if (variantLabel.includes("Mitad")) {
      const parts = variantLabel.split(/(?=Mitad)/g).map((s) => s.trim()).filter(Boolean);
      return (
        <div style={{ marginTop: "2px", paddingLeft: "4px" }}>
          {parts.map((part, idx) => (
            <div key={idx} style={{ fontSize: "12px", fontWeight: "800", color: "#000" }}>
              ▪ {part}
            </div>
          ))}
        </div>
      );
    }

    return (
      <div style={{ fontSize: "12px", fontWeight: "700", marginTop: "1px" }}>
        • {variantLabel}
      </div>
    );
  };

  const rawProducts = getNormalizedProducts(order);
  const productsList = groupProducts(rawProducts);
  const totalItems = productsList.reduce((acc, p) => acc + (p.quantity || 1), 0);
  const orderNum = order.supportCode || (order.id ? order.id.slice(-4).toUpperCase() : "N/A");

  return (
    <div
      ref={ref}
      className="printable-comanda"
      style={{
        color: "#000",
        fontWeight: "bold",
        fontFamily: '"Courier New", Courier, monospace',
        width: "280px",
        margin: "0 auto",
        fontSize: "12px",
        padding: "4px 6px",
        backgroundColor: "#fff",
        lineHeight: "1.2",
        boxSizing: "border-box",
      }}
    >
      {/* 🖨️ CSS DE IMPRESIÓN PARA ELIMINAR EL PAPEL SOBRANTE / DESPERDICIO */}
      <style>{`
        @media print {
          @page {
            size: 80mm auto !important;
            margin: 0mm !important;
          }
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: #fff !important;
            width: 100% !important;
            height: auto !important;
          }
          .printable-comanda {
            width: 100% !important;
            max-width: 80mm !important;
            padding: 2mm 3mm !important;
            margin: 0 !important;
            box-shadow: none !important;
          }
        }
      `}</style>

      {/* Encabezado */}
      <div style={{ textAlign: "center", marginBottom: "6px", borderBottom: "2px solid #000", paddingBottom: "4px" }}>
        <div style={{ fontSize: "17px", fontWeight: "900", letterSpacing: "1px" }}>THOMAS PIZZA</div>
        <div style={{ fontSize: "12px", fontWeight: "900", marginTop: "1px" }}>*** COMANDA DE COCINA ***</div>
      </div>

      {/* Número de Orden y Tipo Destacado */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          border: "2px solid #000",
          padding: "4px 8px",
          marginBottom: "6px",
          backgroundColor: "#f4f4f4",
        }}
      >
        <div>
          <span style={{ fontSize: "10px", display: "block" }}>ORDEN #:</span>
          <span style={{ fontSize: "20px", fontWeight: "900", letterSpacing: "0.5px" }}>#{orderNum}</span>
        </div>
        <div
          style={{
            border: "1.5px solid #000",
            padding: "2px 6px",
            fontSize: "13px",
            fontWeight: "900",
            textAlign: "center",
            backgroundColor: "#fff",
          }}
        >
          {getOrderTypeLabel(order.orderType)}
        </div>
      </div>

      {/* Datos del Pedido */}
      <div style={{ marginBottom: "6px", borderBottom: "1px dashed #000", paddingBottom: "4px", fontSize: "11px" }}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span>FECHA:</span>
          <span>{formatDate(order.date || new Date())}</span>
        </div>
        {(order.customerName || order.name) && (
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: "2px" }}>
            <span>CLIENTE:</span>
            <span style={{ fontWeight: "800", textTransform: "uppercase" }}>{order.customerName || order.name}</span>
          </div>
        )}
        {order.address && (
          <div style={{ marginTop: "2px" }}>
            <span style={{ fontWeight: "800" }}>DIR: </span>
            <span style={{ textTransform: "uppercase" }}>{order.address}</span>
          </div>
        )}
        {(order.masaUserName || order.preparadorName) && (
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: "3px", paddingTop: "3px", borderTop: "1px dotted #aaa" }}>
            {order.masaUserName && (
              <span>ASIGNÓ: <strong>{cleanName(order.masaUserName)}</strong></span>
            )}
            <span>PREP: <strong>{cleanName(order.preparadorName) || "SIN ASIGNAR"}</strong></span>
          </div>
        )}
      </div>

      {/* Notas de la Orden */}
      {order.notes && (
        <div style={{ marginBottom: "6px", border: "1px solid #000", padding: "4px", backgroundColor: "#fff8dc" }}>
          <span style={{ fontWeight: "900", fontSize: "11px" }}>⚠️ NOTAS COCINA:</span>
          <div style={{ fontSize: "12px", fontWeight: "800", marginTop: "2px", whiteSpace: "pre-wrap" }}>
            {order.notes}
          </div>
        </div>
      )}

      {/* Lista de Productos */}
      <div style={{ marginBottom: "6px" }}>
        <div style={{ display: "flex", borderBottom: "1.5px solid #000", paddingBottom: "3px", marginBottom: "4px", fontWeight: "900", fontSize: "11px" }}>
          <span style={{ width: "35px" }}>CANT</span>
          <span style={{ flex: 1 }}>PRODUCTO</span>
        </div>

        {productsList.length === 0 ? (
          <div style={{ fontStyle: "italic", fontSize: "11px" }}>Sin productos registrados</div>
        ) : (
          productsList.map((p, idx) => (
            <div key={idx} style={{ marginBottom: "6px", borderBottom: "1px dotted #bbb", paddingBottom: "4px" }}>
              <div style={{ display: "flex", alignItems: "flex-start" }}>
                <span style={{ width: "35px", fontWeight: "900", fontSize: "16px" }}>{p.quantity}x</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: "900", fontSize: "14px", textTransform: "uppercase" }}>
                    {p.productName}
                  </div>
                  {formatVariant(p.variantLabel)}
                  {p.additions && p.additions.length > 0 && (
                    <div style={{ marginTop: "2px", paddingLeft: "4px" }}>
                      {p.additions.map((add, aIdx) => (
                        <div key={aIdx} style={{ fontSize: "11px", fontWeight: "700" }}>
                          + {add.name}
                        </div>
                      ))}
                    </div>
                  )}
                  {p.description && (
                    <div style={{ fontSize: "11px", fontStyle: "italic", marginTop: "2px", color: "#333" }}>
                      * {p.description}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pie de Comanda */}
      <div style={{ borderTop: "2px solid #000", paddingTop: "4px", textAlign: "center" }}>
        <div style={{ fontSize: "13px", fontWeight: "900" }}>TOTAL PRODUCTOS: {totalItems}</div>
        <div style={{ fontSize: "10px", marginTop: "2px", letterSpacing: "1px" }}>=== FIN DE COMANDA ===</div>
      </div>
    </div>
  );
});

ComandaReceipt.displayName = "ComandaReceipt";

